import { useMemo, useState } from 'react';
import { FaLocationDot } from 'react-icons/fa6';
import { useQuery } from '@tanstack/react-query';

import { storageLocationQueries } from '../../../storage/network/queries.ts';
import { MultiSelectRadioDropdown } from '../components/MultiSelectRadioDropdown';
import {
  useTableFilters,
  type UseTableFiltersArgs,
} from '../hooks/useTableFilters.ts';

export function LocationNameFilter({
  filterName,
  setSearchParams,
  searchParams,
}: {
  filterName: string;
  setSearchParams: UseTableFiltersArgs['setSearchParams'];
  searchParams: URLSearchParams;
}) {
  const [query, setQuery] = useState('');

  const { upsertFilter, removeFilter, getAppliedAndNonAppliedOptions } =
    useTableFilters({
      setSearchParams,
    });
  const filterValues = searchParams.get(filterName)?.split('_');

  const {
    data: locations,
    isError,
    isLoading,
  } = useQuery(
    storageLocationQueries.locations({
      params: {
        paginate: false,
        order: 'asc',
        query,
      },
    }),
  );

  const locationOptions = useMemo(
    () =>
      locations?.map(({ id, location_name }) => ({
        label: location_name,
        value: String(id),
      })),
    [locations],
  );

  const [selectedLocations, setSelectedLocations] = useState(
    filterValues ?? [],
  );
  const { appliedOptions, nonAppliedOptions } = useMemo(
    () => getAppliedAndNonAppliedOptions(filterValues, locationOptions),
    [filterValues, locationOptions, getAppliedAndNonAppliedOptions],
  );

  return (
    <MultiSelectRadioDropdown
      optionsCount={appliedOptions?.length}
      selected={selectedLocations}
      setSelected={setSelectedLocations}
      onSearch={setQuery}
      onReset={() => removeFilter(filterName)}
      name="location"
      label="Location"
      placeholder="Search location name"
      dropdownIcon={<FaLocationDot />}
      nonAppliedOptions={nonAppliedOptions}
      appliedOptions={appliedOptions}
      onSubmit={() =>
        upsertFilter({
          filterName,
          values: selectedLocations,
        })
      }
      isError={isError}
      isLoading={isLoading}
    />
  );
}
